import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import { Box, Card, CircularProgress, FormControl, Grid, MenuItem, Select, Typography, styled } from '@mui/material';
import moment from 'moment';
import { DateRangePicker } from 'rsuite';
import 'rsuite/dist/rsuite.min.css';
import getAxiosInstance from 'api';
import CircularProgressBar from 'ui-component/CircularProgressBar';
import toast from 'react-hot-toast';
import Logs from 'views/usersDetails/Logs';
import {
  appListPayloadDefaults,
  dashboardQuickRanges,
  getDateRangeFromQuickOption,
  getDefaultDashboardDateRange,
  mapAppListItemsForCustomerSelect,
} from 'utils/common';

const StyledCard = styled(Card)(() => ({
  padding: '24px',
  boxShadow: '0px 2px 6px rgba(0, 0, 0, 0.06)',
  borderRadius: '12px',
  border: '1px solid rgba(0, 0, 0, 0.1)',
}));

/** API customer dropdown: min height on the scrollable list + paper */
const customerSelectMenuProps = {
  PaperProps: {
    sx: {
      minHeight: 260,
      maxHeight: 420,
    },
  },
  MenuListProps: {
    sx: {
      minHeight: 240,
      maxHeight: 380,
      overflow: 'auto',
    },
  },
};

const metricMeta = [
  { key: 'total', header: 'Total Calls' },
  { key: 'manual', header: 'Manual Calls' },
  { key: 'digital', header: 'Digital Calls' },
  { key: 'created', header: 'Created Calls' },
  { key: 'success', header: 'Successful Calls' },
  { key: 'failed', header: 'Errors/Failed Calls' },
];

const sectionMeta = [
  { key: 'overall', title: 'Total Calls' },
  { key: 'creditAssessment', title: 'Credit Assessment' },
  { key: 'cac', title: 'CAC' },
  { key: 'bvn', title: 'BVN' },
  { key: 'nin', title: 'NIN' },
];

const sectionsWithoutCreatedRing = new Set(['cac', 'bvn', 'nin']);
const metricApiMap = {
  manual: 'manual',
  digital: 'digital',
  success: 'successful',
  failed: 'failed',
};
/** Resolves to e.g. {REACT_APP_EDOC_API_URL}/adminOrchestrator/dashboard/getProductWiseConsentCountsByPartner */
const PRODUCT_CALL_COUNTS_API =
  'adminOrchestrator/dashboard/getProductWiseConsentCountsByPartner';

const PRODUCT_KEYS = ['creditAssessment', 'nin', 'bvn', 'cac'];

const toCount = (value) => (Number.isFinite(Number(value)) ? Number(value) : 0);

const getFirstCount = (source, keys) => {
  for (const key of keys) {
    if (source?.[key] !== undefined && source?.[key] !== null && source?.[key] !== '') {
      return toCount(source[key]);
    }
  }
  return 0;
};

/** Maps UI metric keys to API field names (new nested response + legacy flat keys). */
const sectionMetricCandidates = {
  overall: {
    total: ['totalCalls', 'totalCallCount', 'totalRequestCount'],
    manual: ['manualCalls', 'manualCallCount'],
    digital: ['digitalCalls', 'digitalCallCount'],
    created: ['createdCalls', 'createdCallCount', 'createdRequestCount'],
    success: ['successfulCalls', 'successfulCallCount'],
    failed: ['failedCalls', 'failedCallCount', 'failedRequestCount'],
  },
  creditAssessment: {
    total: ['totalCalls', 'creditAssessmentTotalCallCount', 'consentTotalCallCount', 'creditRiskCount'],
    manual: ['manualCalls', 'creditAssessmentManualCallCount', 'consentManualCallCount'],
    digital: ['digitalCalls', 'creditAssessmentDigitalCallCount', 'consentDigitalCallCount'],
    created: ['createdCalls', 'creditAssessmentCreatedCallCount', 'consentCreatedCallCount'],
    success: ['successfulCalls', 'creditAssessmentSuccessfulCallCount', 'successConsentRequest'],
    failed: ['failedCalls', 'creditAssessmentFailedCallCount', 'consentFailedCallCount'],
  },
  cac: {
    total: ['totalCalls', 'cacTotalCallCount', 'cacCount'],
    manual: ['manualCalls', 'cacManualCallCount', 'cacManualUploadedCallCount'],
    digital: ['digitalCalls', 'cacDigitalCallCount'],
    created: [],
    success: ['successfulCalls', 'cacSuccessfulCallCount', 'successCACRequest'],
    failed: ['failedCalls', 'cacFailedCallCount', 'failedCACRequestCount'],
  },
  bvn: {
    total: ['totalCalls', 'bvnTotalCallCount', 'bvnCount'],
    manual: ['manualCalls', 'bvnManualCallCount', 'bvnManualUploadedCallCount'],
    digital: ['digitalCalls', 'bvnDigitalCallCount'],
    created: [],
    success: ['successfulCalls', 'bvnSuccessfulCallCount', 'successBVNRequest'],
    failed: ['failedCalls', 'bvnFailedCallCount', 'failedBVNRequestCount'],
  },
  nin: {
    total: ['totalCalls', 'ninTotalCallCount', 'ninCount'],
    manual: ['manualCalls', 'ninManualCallCount', 'ninManualUploadedCallCount'],
    digital: ['digitalCalls', 'ninDigitalCallCount'],
    created: [],
    success: ['successfulCalls', 'ninSuccessfulCallCount', 'successNINRequest'],
    failed: ['failedCalls', 'ninFailedCallCount', 'failedNINRequestCount'],
  },
};

function aggregateOverallFromProducts(data) {
  const empty = {
    totalCalls: 0,
    manualCalls: 0,
    digitalCalls: 0,
    createdCalls: 0,
    successfulCalls: 0,
    failedCalls: 0,
  };
  if (!data || typeof data !== 'object') {
    return empty;
  }
  return PRODUCT_KEYS.reduce((acc, productKey) => {
    const p = data[productKey] || {};
    acc.totalCalls += toCount(p.totalCalls);
    acc.manualCalls += toCount(p.manualCalls);
    acc.digitalCalls += toCount(p.digitalCalls);
    acc.createdCalls += toCount(p.createdCalls);
    acc.successfulCalls += toCount(p.successfulCalls);
    acc.failedCalls += toCount(p.failedCalls);
    return acc;
  }, { ...empty });
}

const CustomerCallsTab = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [customers, setCustomers] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState('');
  const [callsQuickRange, setCallsQuickRange] = useState('');
  const [selectedDate, setSelectedDate] = useState(() => getDefaultDashboardDateRange());
  const [stats, setStats] = useState({});
  const [loadingCustomers, setLoadingCustomers] = useState(false);
  const [loadingStats, setLoadingStats] = useState(false);

  useEffect(() => {
    setLoadingCustomers(true);
    getAxiosInstance(
      'adminOrchestrator/app/list',
      'post',
      appListPayloadDefaults,
      { 'journey-type': 'Dashboard' }
    )
      .then((res) => {
        if (res?.data?.codes === 200) {
          const items = res?.data?.data?.items || [];
          setCustomers(mapAppListItemsForCustomerSelect(items));
        }
      })
      .catch((err) => {
        toast.error(err?.response?.data?.message || 'Failed to load API customers');
      })
      .finally(() => setLoadingCustomers(false));
  }, []);

  useEffect(() => {
    setLoadingStats(true);
    const payload = {
      startDate: moment(selectedDate[0]).format('MM-DD-YYYY'),
      endDate: moment(selectedDate[1]).format('MM-DD-YYYY'),
    };
    if (selectedCustomer) {
      payload.businessPartnerId = selectedCustomer;
    }
    getAxiosInstance(
      PRODUCT_CALL_COUNTS_API,
      'post',
      payload,
      { 'journey-type': 'Dashboard' }
    )
      .then((res) => {
        if (res?.data?.codes === 200 && res?.data?.data) {
          const data = res.data.data;
          setStats({
            overall: aggregateOverallFromProducts(data),
            creditAssessment: data.creditAssessment || {},
            nin: data.nin || {},
            bvn: data.bvn || {},
            cac: data.cac || {},
          });
        } else {
          setStats({});
        }
      })
      .catch((err) => {
        toast.error(err?.response?.data?.message || 'Failed to load call counts');
        setStats({});
      })
      .finally(() => setLoadingStats(false));
  }, [selectedCustomer, selectedDate]);
  const handleViewDrilldown = (metricKey) => {

    const metricType = metricApiMap[metricKey];
    navigate('/consent-drilldown', {
      state: {
        metric: metricType,
        businessPartnerId: selectedCustomer || '',
        returnPath: location.pathname,
        returnState: { activeTab: 1 },
      },
    });};
  const sections = useMemo(() => {
    return sectionMeta.map((section) => {
      const candidates = sectionMetricCandidates[section.key] || {};
      const scope = section.key === 'overall' ? stats.overall : stats[section.key];
      const source = scope && typeof scope === 'object' ? scope : {};
      const values = {
        total: getFirstCount(source, candidates.total || []),
        manual: getFirstCount(source, candidates.manual || []),
        digital: getFirstCount(source, candidates.digital || []),
        created: getFirstCount(source, candidates.created || []),
        success: getFirstCount(source, candidates.success || []),
        failed: getFirstCount(source, candidates.failed || []),
      };

      if (section.key !== 'overall' && values.total === 0) {
        values.total = values.success + values.failed;
      }

      return { ...section, values };
    });
  }, [stats]);

  return (
    <Box sx={{ p: 3 }}>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: { xs: 'flex-start', md: 'center' },
          flexDirection: { xs: 'column', md: 'row' },
          gap: 2,
          mb: 3,
        }}
      >
        <Typography variant="h4" sx={{ fontWeight: 500, color: '#212120' }}>
          Count Dashboard
        </Typography>
        <Box
          sx={{
            display: 'flex',
            gap: 2,
            alignItems: 'center',
            flexWrap: 'wrap',
          }}
        >
          <FormControl size="small" sx={{ minWidth: 260 }}>
            <Select
              value={selectedCustomer}
              onChange={(e) => {
                const id = String(e.target.value);
                setSelectedCustomer(id);
                if (id) {
                  setSelectedDate(getDefaultDashboardDateRange());
                }
              }}
              displayEmpty
              disabled={loadingCustomers}
              MenuProps={customerSelectMenuProps}
              sx={{ backgroundColor: '#fff' }}
            >
              <MenuItem value="">Select API Customer</MenuItem>
              {customers.map((customer) => (
                <MenuItem key={customer.id} value={String(customer.id)}>
                  {customer.companyName || customer.emailAddress || customer.id}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 170 }}>
            <Select
              value={callsQuickRange}
              onChange={(e) => {
                const value = e.target.value;
                setCallsQuickRange(value);
                if (value) {
                  const range = getDateRangeFromQuickOption(value);
                  if (range) {
                    setSelectedDate([range.start, range.end]);
                  }
                }
              }}
              displayEmpty
              sx={{
                backgroundColor: '#fff',
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: 'rgba(0, 0, 0, 0.23)',
                },
              }}
            >
              <MenuItem value="">Quick Range</MenuItem>
              {dashboardQuickRanges.map((range) => (
                <MenuItem key={range.value} value={range.value}>
                  {range.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <DateRangePicker
            appearance="default"
            placeholder="Select Date Range"
            value={selectedDate}
            onChange={(value) => {
              if (!value) return;
              setSelectedDate([value[0], value[1]]);
            }}
            showOneCalendar
          />
        </Box>
      </Box>

      {loadingStats || (loadingCustomers && !selectedCustomer) ? (
        <StyledCard>
          <Box display="flex" justifyContent="center" py={5}>
            <CircularProgress color="secondary" />
          </Box>
        </StyledCard>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          {sections.map((section) => (
            <StyledCard key={section.key}>
              <Typography
                variant="h4"
                sx={{ fontWeight: 500, color: '#212120', mb: 2 }}
              >
                {section.title}
              </Typography>
              <Grid container spacing={4} justifyContent="flex-start">
                {metricMeta
                  .filter(
                    (metric) =>
                      metric.key !== 'created' ||
                      !sectionsWithoutCreatedRing.has(section.key)
                  )
                  .map((metric) => (
                  <Grid
                    item
                    xs={12}
                    sm={6}
                    md={2}
                    key={`${section.key}-${metric.key}`}
                    sx={{ textAlign: 'center' }}
                  >
                      <Box sx={{ maxWidth: 200, margin: '0 auto' }}>
                        <CircularProgressBar
                          width={100}
                          size={150}
                          value={section.values[metric.key]}
                          progress={100}
                          title={metric.header}
                        />

                        {section.key === 'creditAssessment' &&
                          !['total', 'created'].includes(metric.key) && (

                            <Box
                              onClick={() => handleViewDrilldown(metric.key)}
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                gap: '2px',
                                mt: 1,
                                cursor: 'pointer',
                                userSelect: 'none',
                              }}
                            >
                              <KeyboardArrowUpIcon
                                sx={{
                                  color: '#FC5719',
                                  fontSize: '16px',
                                  transform: 'translateY(-1px)',
                                }}
                              />

                              <Typography
                                sx={{
                                  color: '#FC5719',
                                  fontSize: '15px',
                                  fontWeight: 600,
                                  lineHeight: 1,
                                }}
                              >
                                View Drilldown
                              </Typography>
                            </Box>

                          )}
                      </Box>
                  </Grid>
                  ))}
              </Grid>
            </StyledCard>
          ))}
            <Box mt={3}>
              <Logs
                selectedDate={selectedDate}
                businessPartnerId={selectedCustomer}
                showProduct
              />
            </Box>
        </Box>
      )}
    </Box>
  );
};

export default CustomerCallsTab;
